// @flow

import React from 'react';
import type { Node } from 'react';
import styled from 'styled-components';
import { Carousel, ImageLoader, Link } from 'components/Gui';
import type { CarouselConsumerProps } from 'components/Gui';
import { absolute, Color, flex, hoverChild, p1524, pDefault, topPadding, Transition, Width } from 'styles';
import { RightChevronArrow } from 'components/Svg';
import type { AllImages } from 'hooks/use-images';
import { useImages } from 'hooks';
import { labelToId } from 'utils';

const Wrapper = styled.section`
  ${topPadding(64, 54, 118)};
  ${flex('flex-start', 'center')};
  flex-direction: column;

  h2,
  h3 {
    text-align: center;
  }
`;

const CarouselWrapper = styled.div`
  width: 100%;
  height: 50rem;
  flex: 0 0 auto;
  position: relative;
  max-width: 400px;

  @media print, screen and (min-width: ${Width.BREAK_MD}px) {
    max-width: 700px;
    height: 58rem;
  }

  @media print, screen and (min-width: ${Width.BREAK_XL}px) {
    max-width: 900px;
    height: 34rem;
  }
`;

const Slide = styled.div`
  ${absolute()};
  transform: translateX(${({ positionX }) => `${positionX * 100}vw`});
  transition: ${({ opacity }) => (opacity ? `transform 500ms ${Transition.EASE_MOBILE}` : `none`)};
  opacity: ${({ opacity }) => opacity};
  ${flex('flex-start', 'center')};
  flex-direction: column;
  margin-top: 40px;

  @media print, screen and (min-width: ${Width.BREAK_MD}px) {
    margin-top: 50px;
  }

  @media print, screen and (min-width: ${Width.BREAK_XL}px) {
    flex-direction: row;
    justify-content: center;
    align-items: flex-start;
  }

  > div {
    &:first-of-type {
      width: 205px;
    }
    &:last-of-type {
      margin-top: 40px;
      width: 100%;
      text-align: center;

      @media print, screen and (min-width: ${Width.BREAK_MD}px) {
        max-width: 54rem;
      }

      @media print, screen and (min-width: ${Width.BREAK_XL}px) {
        max-width: fit-content;
        width: 50rem;
        flex: 0 0 auto;
        margin-top: 0;
        margin-left: 62px;
        text-align: left;
      }

      p {
        ${p1524};

        span {
          display: block;
          margin-top: 16px;
          ${pDefault};
        }

        @media print, screen and (min-width: ${Width.BREAK_MD}px) {
          font-weight: 300;

          span {
            font-weight: 400;
          }
        }
      }
    }
  }
`;

const Buttons = styled.div`
  ${absolute(0, `-${Width.MARGIN_SMALL}px`, 'unset', `-${Width.MARGIN_SMALL}px`)};
  height: 280px;
  ${flex('space-between', 'center')};
  pointer-events: none;
  z-index: 1;

  > button {
    height: 48px;
    width: 48px;
    ${flex()};
    ${hoverChild('path', 'stroke', '#8290D7', Color.PURPLE_FIGURE, true)};
    pointer-events: auto;

    &:first-of-type {
      transform: rotate(180deg);
    }

    svg {
      width: 10px;
    }
  }
`;

type Props = {
  title: Node | string,
  label: string,
  slides: any[],
  images: AllImages,
};

const TestimonialCarousel = ({ title, label, slides, images, ...rest }: Props) => {
  const id = labelToId(label);
  const getImage = useImages(images);
  return (
    <Wrapper {...rest}>
      {title}
      <Carousel data={slides} label={label}>
        {({ changeSlide, currentIndex, slideContent, slideOpacity, slidePositionX, onScreen }: CarouselConsumerProps) => (
          <CarouselWrapper aria-live="polite" id={id}>
            {slideContent.map(({ image: { name: imageName, alt }, text, name }, index) => (
              <Slide
                aria-hidden={slidePositionX[index] !== 0}
                aria-roledescription="slide"
                role="group"
                aria-label={`Testiominal of ${name}`}
                key={`TestimonialCarouselSlide-${index}`}
                current={currentIndex === index}
                opacity={slideOpacity[index]}
                positionX={slidePositionX[index]}>
                <div>
                  <ImageLoader image={getImage(imageName)} alt={alt} overrideLoading={onScreen} />
                </div>
                <div>
                  <p>
                    {`“${text}”`}
                    <br />
                    <span>{`— ${name}`}</span>
                  </p>
                </div>
              </Slide>
            ))}
            <Buttons>
              <Link aria-controls={id} label={`Previous Slide`} onClick={() => changeSlide()}>
                {RightChevronArrow}
              </Link>
              <Link aria-controls={id} label={`Next Slide`} onClick={() => changeSlide(true)}>
                {RightChevronArrow}
              </Link>
            </Buttons>
          </CarouselWrapper>
        )}
      </Carousel>
    </Wrapper>
  );
};

export default TestimonialCarousel;
